import React, { useState, useContext } from "react";
import { Modal, Button, List, Typography, Space, Tag, Skeleton } from "antd";
import {
  CheckCircleOutlined,
  BankOutlined,
  HomeOutlined,
  UserOutlined,
  ArrowRightOutlined,
  CloseOutlined,
  RocketOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import styled, { createGlobalStyle } from "styled-components";
import { AuthContext } from "../context/AuthProvider";
import { device } from "../lib/breakpoints";
import { trackEvent } from "../helpers/mixpanel";
import userModel from "../lib/firebase/userModel";
import { useQueryClient } from "@tanstack/react-query";
import { userKeys } from "../lib/queryKeys";
import SettingsModel from "../lib/firebase/settingsModel";
import unitModel from "../lib/firebase/unitModel";
import { listTenants } from "../lib/firebase/tenantModel";
import { useConnectStatus } from "../hooks/useConnectStatus";

const { Text, Title } = Typography;

const ICON_IN_GREEN_CIRCLE = { fontSize: 18, filter: "brightness(0) invert(1)" };
const CHECK_ON_GREEN = { color: "#fff" };

const LoFirstTimeModalCloseColor = createGlobalStyle`
  .lo-firsttime-modal .ant-modal-close,
  .lo-firsttime-modal .ant-modal-close .anticon,
  .lo-firsttime-modal .ant-modal-close-x {
    color: rgba(0, 0, 0, 0.88) !important;
  }
  .lo-firsttime-modal .ant-modal-close:hover,
  .lo-firsttime-modal .ant-modal-close:hover .anticon,
  .lo-firsttime-modal .ant-modal-close-x:hover {
    color: #000 !important;
  }
`;

const FirstTimeUserModal = ({ isOpen, onClose }) => {
  const { user, userInfo } = useContext(AuthContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [tasksLoading, setTasksLoading] = useState(true);

  // Check Connect status for real-time payment capability
  const { canAcceptPayments } = useConnectStatus(user?.uid);

  // Check completion status for each task
  const [taskStatus, setTaskStatus] = useState({
    bankAccount: false,
    property: false,
    tenant: false,
  });

  React.useEffect(() => {
    if (!isOpen || !user) return;

    const checkTaskStatus = async () => {
      setTasksLoading(true);
      try {
        // Check bank account setup - use canAcceptPayments from useConnectStatus
        // This ensures consistency with Settings page
        const bankAccountSetup = canAcceptPayments || false;

        // Check if user has properties/units
        const units = await unitModel.getByOwner(user.uid);
        const hasProperty = units && units.length > 0;

        // Check if user has tenants
        const tenantsResult = await listTenants();
        const hasTenant =
          tenantsResult?.tenants && tenantsResult.tenants.length > 0;

        setTaskStatus({
          bankAccount: bankAccountSetup,
          property: hasProperty,
          tenant: hasTenant,
        });
      } catch (error) {
        console.error("Error checking task status:", error);
      } finally {
        setTasksLoading(false);
      }
    };

    checkTaskStatus();
  }, [isOpen, user, canAcceptPayments]);

  const handleDismiss = async () => {
    setLoading(true);
    try {
      trackEvent("first_time_modal_dismissed", {
        user_id: user?.uid,
      });

      onClose();
    } catch (error) {
      console.error("Error dismissing modal:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOptionalImportClick = () => {
    trackEvent("first_time_modal_optional_import_csv_clicked", {
      user_id: user?.uid,
    });

    // Route to dashboard and open the import modal from there.
    // Dashboard listens for this state in `location.state`.
    navigate("/dashboard", { state: { openImportPortfolioModal: true } });
    onClose();
  };

  const handleTaskClick = (taskKey) => {
    trackEvent("first_time_modal_task_clicked", {
      task: taskKey,
      user_id: user?.uid,
    });

    switch (taskKey) {
      case "bankAccount":
        navigate("/settings#payment-setup");
        break;
      case "property":
        navigate("/units");
        break;
      case "tenant":
        navigate("/tenants");
        break;
      default:
        break;
    }

    // Close modal when navigating
    onClose();
  };

  const tasks = [
    {
      key: "bankAccount",
      icon: <BankOutlined style={ICON_IN_GREEN_CIRCLE} />,
      title: "Set up your bank account",
      description: "Connect Stripe to accept rent payments from tenants",
      completed: taskStatus.bankAccount,
      action: "Go to Payment Setup",
    },
    {
      key: "property",
      icon: <HomeOutlined style={ICON_IN_GREEN_CIRCLE} />,
      title: "Add a property",
      description: "Add properties and doors to start managing your portfolio",
      completed: taskStatus.property,
      action: "Add Property",
    },
    {
      key: "tenant",
      icon: <UserOutlined style={ICON_IN_GREEN_CIRCLE} />,
      title: "Add your tenants",
      description:
        "Add tenants and assign them to a door. Tenants must have a door assigned to receive payment emails and reminders.",
      completed: taskStatus.tenant,
      action: "Add Tenant",
    },
  ];

  const completedCount = tasks.filter((t) => t.completed).length;
  const totalTasks = tasks.length;
  const remainingTasks = totalTasks - completedCount;

  return (
    <>
      {isOpen ? <LoFirstTimeModalCloseColor /> : null}
      <StyledModal
        open={isOpen}
        onCancel={handleDismiss}
        footer={null}
        closable
        width={600}
        centered
        maskClosable
        rootClassName="lo-firsttime-modal"
        closeIcon={
          <CloseOutlined style={{ color: "rgba(0, 0, 0, 0.88)", fontSize: 16 }} />
        }
        styles={{ body: { padding: 0 } }}
      >
      <ModalContent>
        <HeaderSection>
          <Title level={3}>Quick Start Guide</Title>
          <SubheaderText type="secondary">
            Complete these essential steps to unlock the full power of
            LandlordOps
          </SubheaderText>
        </HeaderSection>

        <HeroSection>
          <HeroIcon>
            <RocketOutlined style={{ filter: "brightness(0) invert(1)" }} />
          </HeroIcon>
          <HeroContent>
            <HeroText>
              <HeroTitle>Get Started</HeroTitle>
              <HeroSubtitle>
                Finish these steps to enable automated payments, AI assistance,
                and seamless tenant communication.
              </HeroSubtitle>
            </HeroText>
            <HeroBadge $isComplete={remainingTasks === 0}>
              {remainingTasks === 0
                ? "All set"
                : `${remainingTasks} step${
                    remainingTasks === 1 ? "" : "s"
                  } remaining`}
            </HeroBadge>
          </HeroContent>
        </HeroSection>

        {!tasksLoading && (
          <ProgressSection>
            <ProgressText>
              {completedCount} of {totalTasks} steps completed
            </ProgressText>
            <ProgressBar>
              <ProgressFill width={(completedCount / totalTasks) * 100} />
            </ProgressBar>
          </ProgressSection>
        )}

        <TasksList>
          {tasksLoading
            ? // Show skeleton placeholders while loading
              [1, 2, 3].map((index) => (
                <TaskSkeleton key={index}>
                  <TaskSkeletonLeft>
                    <Skeleton.Avatar active size={40} shape="circle" />
                    <TaskSkeletonContent>
                      <Skeleton
                        active
                        paragraph={{ rows: 2, width: ["100%", "80%"] }}
                        title={false}
                      />
                    </TaskSkeletonContent>
                  </TaskSkeletonLeft>
                  <Skeleton.Avatar active size={38} shape="circle" />
                </TaskSkeleton>
              ))
            : tasks.map((task) => (
                <TaskItem
                  as="button"
                  type="button"
                  key={task.key}
                  completed={task.completed}
                  onClick={() => {
                    if (!task.completed) {
                      handleTaskClick(task.key);
                    }
                  }}
                  disabled={task.completed}
                >
                  <TaskLeft>
                    <TaskIcon completed={task.completed}>
                      {task.completed ? <CheckCircleOutlined /> : task.icon}
                      {!task.completed && <TaskBadge />}
                    </TaskIcon>
                    <TaskContent>
                      <TaskHeader>
                        <TaskTitle completed={task.completed}>
                          {task.title}
                        </TaskTitle>
                        <TaskStatusChip $completed={task.completed}>
                          {task.completed ? "Complete" : "To do"}
                        </TaskStatusChip>
                      </TaskHeader>
                      <TaskDescription>{task.description}</TaskDescription>
                    </TaskContent>
                  </TaskLeft>

                  {!task.completed && (
                    <TaskActionIcon aria-hidden="true">
                      <ArrowRightOutlined />
                    </TaskActionIcon>
                  )}
                </TaskItem>
              ))}
        </TasksList>

        <OptionalSection>
          <OptionalTitle>Optional</OptionalTitle>

          <OptionalTaskItem type="button" onClick={handleOptionalImportClick}>
            <TaskLeft>
              <TaskIcon>
                <UploadOutlined style={ICON_IN_GREEN_CIRCLE} />
              </TaskIcon>

              <TaskContent>
                <TaskHeader>
                  <TaskTitle completed={false}>
                    Import your portfolio (CSV)
                  </TaskTitle>
                  <OptionalStatusChip>Optional</OptionalStatusChip>
                </TaskHeader>
                <TaskDescription>
                  Bulk-load properties, units, and tenants from a rent roll CSV.
                </TaskDescription>
              </TaskContent>
            </TaskLeft>

            <TaskActionIcon aria-hidden="true">
              <ArrowRightOutlined />
            </TaskActionIcon>
          </OptionalTaskItem>
        </OptionalSection>

        <FooterSection>
          <Button
            type="primary"
            onClick={handleDismiss}
            loading={loading}
            size="large"
          >
            Got it!
          </Button>
        </FooterSection>
      </ModalContent>
    </StyledModal>
    </>
  );
};

const StyledModal = styled(Modal)`
  z-index: 10000 !important;

  .ant-modal-mask {
    z-index: 9999 !important;
  }

  .ant-modal-wrap {
    z-index: 10000 !important;
    /* When modal is tall, allow scrolling the overlay instead of clipping the top */
    align-items: center;
    padding: 16px 0;
    overflow: auto;
  }

  .ant-modal {
    z-index: 10001 !important;
    max-width: calc(100vw - 32px);
  }

  .ant-modal-content {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    max-height: min(90dvh, calc(100vh - 32px));
    display: flex;
    flex-direction: column;

    @media ${device.mobile} {
      border-radius: 16px 16px 0 0;
      margin: 0;
      max-width: 100vw;
      max-height: min(92dvh, calc(100vh - 16px));
    }
  }

  .ant-modal-header {
    display: none;
  }

  .ant-modal-body {
    padding: 0;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
  }

  .ant-modal-close {
    top: 16px;
    right: 16px;
    z-index: 10;
    color: rgba(0, 0, 0, 0.88);

    .ant-modal-close-x,
    .anticon {
      color: rgba(0, 0, 0, 0.88);
    }

    &:hover,
    &:hover .ant-modal-close-x,
    &:hover .anticon {
      color: #000;
    }

    @media ${device.mobile} {
      top: 12px;
      right: 12px;
      width: 32px;
      height: 32px;
    }
  }

  @media ${device.mobile} {
    top: 0;
    padding-bottom: 0;
    max-width: 100vw;

    .ant-modal {
      margin: 0;
      max-width: 100vw;
      top: 0;
    }
  }
`;

const ModalContent = styled.div`
  padding: 2rem;
  /* Body scrolls; avoid double scroll + keep width unchanged */
  min-height: 0;

  @media ${device.mobile} {
    padding: 1.25rem 1rem;
  }
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 2rem;

  h3 {
    margin-bottom: 0.5rem;
    color: var(--color-text);
    font-size: 1.75rem;
  }

  @media ${device.mobile} {
    margin-bottom: 1.25rem;
    padding: 0 0.5rem;

    h3 {
      font-size: 1.5rem;
      line-height: 1.3;
      margin-bottom: 0.375rem;
    }

    p {
      font-size: 0.9rem;
      line-height: 1.4;
    }
  }
`;

const SubheaderText = styled(Text)`
  @media (min-width: 768px) {
    white-space: nowrap;
    display: block;
  }

  @media ${device.mobile} {
    white-space: normal;
  }
`;

const ProgressSection = styled.div`
  margin-bottom: 2rem;

  @media ${device.mobile} {
    margin-bottom: 1.5rem;
  }
`;

const ProgressText = styled.div`
  font-size: 0.875rem;
  color: var(--color-text-light);
  margin-bottom: 0.5rem;
  font-weight: 500;

  @media ${device.mobile} {
    font-size: 0.8rem;
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 10px;
  background: rgba(15, 118, 110, 0.12);
  border-radius: 999px;
  overflow: hidden;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.08);
  position: relative;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${(props) => Math.min(props.width, 100)}%;
  background: linear-gradient(90deg, #4bc069 0%, #2f855a 100%);
  border-radius: inherit;
  transition: width 0.4s ease;
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -60%;
    width: 60%;
    height: 100%;
    background: linear-gradient(
      120deg,
      transparent 0%,
      rgba(255, 255, 255, 0.8) 50%,
      transparent 100%
    );
    animation: progressShine 2.6s ease-in-out infinite;
    opacity: 0.8;
  }

  @keyframes progressShine {
    0% {
      transform: translateX(-100%);
      opacity: 0;
    }
    15% {
      opacity: 0.9;
    }
    50% {
      transform: translateX(180%);
      opacity: 0.6;
    }
    100% {
      transform: translateX(220%);
      opacity: 0;
    }
  }
`;

const TasksList = styled.div`
  margin-bottom: 2rem;

  @media ${device.mobile} {
    margin-bottom: 1.5rem;
  }
`;

const OptionalSection = styled.div`
  margin-bottom: 1.5rem;

  @media ${device.mobile} {
    margin-bottom: 1.25rem;
  }
`;

const OptionalTitle = styled.div`
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--color-text-light);
  text-transform: uppercase;
  margin-bottom: 0.75rem;
`;

const OptionalTaskItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  padding: 1.25rem;
  margin-bottom: 0.75rem;
  border: 2px solid var(--color-border-light);
  border-radius: 12px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;
  outline: none;

  &:hover {
    border-color: var(--color-primary);
    box-shadow: 0 6px 16px rgba(44, 95, 45, 0.12);
    transform: translateY(-2px);
  }

  @media ${device.mobile} {
    padding: 1rem;
    margin-bottom: 0.625rem;
    gap: 0.75rem;
    min-height: 80px;

    &:active {
      transform: scale(0.99);
    }
  }
`;

const OptionalStatusChip = styled(Tag)`
  border-radius: 999px;
  padding: 0 0.75rem;
  border-color: transparent !important;
  font-size: 0.75rem;
  background: rgba(44, 95, 45, 0.12);
  color: var(--color-primary);
  flex-shrink: 0;
  height: 22px;
  line-height: 22px;
  margin: 0;
`;

const TaskSkeleton = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  padding: 1.25rem;
  margin-bottom: 0.75rem;
  border: 2px solid var(--color-border-light);
  border-radius: 12px;
  background: white;

  @media ${device.mobile} {
    padding: 1rem;
    margin-bottom: 0.625rem;
  }
`;

const TaskSkeletonLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex: 1;

  @media ${device.mobile} {
    gap: 0.75rem;
  }
`;

const TaskSkeletonContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const TaskItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  padding: 1.25rem;
  margin-bottom: 0.75rem;
  border: 2px solid var(--color-border-light);
  border-radius: 12px;
  background: white;
  cursor: ${(props) => (props.completed ? "default" : "pointer")};
  transition: all 0.2s ease;
  text-align: left;
  outline: none;

  &:disabled {
    cursor: default;
    opacity: 1;
    background: white;
    color: inherit;
  }

  &:hover {
    ${(props) =>
      !props.completed &&
      `
      border-color: var(--color-primary);
      box-shadow: 0 6px 16px rgba(44, 95, 45, 0.12);
      transform: translateY(-2px);
    `}
  }

  @media ${device.mobile} {
    padding: 1rem;
    margin-bottom: 0.625rem;
    gap: 0.75rem;
    min-height: 80px;

    &:active {
      ${(props) =>
        !props.completed &&
        `
        transform: scale(0.98);
      `}
    }
  }
`;

const TaskLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex: 1;
  min-width: 0;

  @media ${device.mobile} {
    gap: 0.75rem;
  }
`;

const TaskIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  margin-right: 1rem;
  background: ${(props) =>
    props.completed ? "transparent" : "var(--color-primary)"};
  flex-shrink: 0;
  position: relative;

  .anticon {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 100% !important;
    height: 100% !important;
    color: ${(props) =>
      props.completed ? "var(--color-primary)" : "inherit"};
  }

  .anticon svg {
    width: ${(props) => (props.completed ? "40px" : "1.25rem")} !important;
    height: ${(props) => (props.completed ? "40px" : "1.25rem")} !important;
  }

  @media ${device.mobile} {
    width: 36px;
    height: 36px;
    margin-right: 0.75rem;
    font-size: 1.1rem;

    .anticon svg {
      width: ${(props) => (props.completed ? "36px" : "1.1rem")} !important;
      height: ${(props) => (props.completed ? "36px" : "1.1rem")} !important;
    }
  }
`;

const TaskBadge = styled.div`
  position: absolute;
  top: -2px;
  right: -2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ff4d4f;
  border: 2px solid white;
  z-index: 10;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
`;

const TaskContent = styled.div`
  flex: 1;
  min-width: 0;

  @media ${device.mobile} {
    flex: 1 1 auto;
  }
`;

const TaskHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 0.25rem;

  @media ${device.mobile} {
    flex-direction: column;
    align-items: flex-start;
    flex-wrap: nowrap;
    gap: 0.375rem;
    margin-bottom: 0.375rem;
  }
`;

const TaskTitle = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: ${(props) =>
    props.completed ? "var(--color-primary)" : "var(--color-text)"};
  margin-bottom: 0;
  text-decoration: ${(props) => (props.completed ? "line-through" : "none")};
  opacity: ${(props) => (props.completed ? 0.7 : 1)};
  line-height: 1.4;

  @media ${device.mobile} {
    font-size: 0.95rem;
    line-height: 1.3;
    width: 100%;
    min-width: 0;
    overflow-wrap: break-word;
    word-break: normal;
  }
`;

const TaskDescription = styled.div`
  font-size: 0.875rem;
  color: var(--color-text-light);
  line-height: 1.4;

  @media ${device.mobile} {
    font-size: 0.8rem;
    line-height: 1.35;
  }
`;

const TaskStatusChip = styled(Tag)`
  border-radius: 999px;
  padding: 0 0.75rem;
  border-color: transparent !important;
  font-size: 0.75rem;
  background: ${(props) =>
    props.$completed ? "rgba(44, 95, 45, 0.15)" : "rgba(244, 187, 67, 0.25)"};
  color: ${(props) => (props.$completed ? "var(--color-primary)" : "#9b5b00")};
  flex-shrink: 0;
  height: 22px;
  line-height: 22px;
  margin: 0;

  @media ${device.mobile} {
    font-size: 0.7rem;
    padding: 0 0.625rem;
    height: 20px;
    line-height: 20px;
  }
`;

const TaskActionIcon = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 2px solid var(--color-primary);
  color: var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: rgba(44, 95, 45, 0.06);

  .anticon {
    font-size: 1rem;
  }

  @media ${device.mobile} {
    width: 32px;
    height: 32px;

    .anticon {
      font-size: 0.875rem;
    }
  }
`;

const HeroSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem;
  margin-bottom: 1.75rem;
  border-radius: 16px;
  background: linear-gradient(
    135deg,
    rgba(44, 95, 45, 0.08),
    rgba(44, 95, 45, 0.02)
  );
  border: 1px solid rgba(44, 95, 45, 0.1);

  @media ${device.mobile} {
    gap: 0.75rem;
    padding: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const HeroIcon = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: var(--color-primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  flex-shrink: 0;

  @media ${device.mobile} {
    width: 44px;
    height: 44px;
    font-size: 1.25rem;
  }
`;

const HeroContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  @media ${device.mobile} {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.625rem;
  }
`;

const HeroText = styled.div`
  flex: 1;
`;

const HeroTitle = styled.div`
  font-weight: 600;
  font-size: 1rem;
  color: var(--color-text);
  margin-bottom: 0.25rem;

  @media ${device.mobile} {
    font-size: 0.95rem;
  }
`;

const HeroSubtitle = styled.div`
  font-size: 0.85rem;
  color: var(--color-text-light);
  line-height: 1.4;

  @media ${device.mobile} {
    font-size: 0.8rem;
    line-height: 1.35;
  }
`;

const HeroBadge = styled.div`
  background: ${(props) =>
    props.$isComplete ? "rgba(44,95,45,0.1)" : "rgba(244, 187, 67, 0.25)"};
  color: ${(props) => (props.$isComplete ? "var(--color-primary)" : "#9b5b00")};
  font-weight: 600;
  border-radius: 999px;
  padding: 0.4rem 0.9rem;
  font-size: 0.85rem;
`;

const FooterSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 1.5rem;
  border-top: 1px solid var(--color-border-light);

  @media ${device.mobile} {
    padding-top: 1.25rem;

    button {
      width: 100%;
      height: 48px;
      font-size: 1rem;
    }
  }
`;

export default FirstTimeUserModal;
